import React, { useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { FieldGroup, InputGroup, InputPassword } from "../Input";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Checkbox from "../Checkbox";
import NavTab from "../NavTab";
import Button from "../Button";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import useTranslation from "../../hooks/useTranslation";
import {
  validateEmail,
  validUsername,
  validLoginField,
  validPassword,
  checkComfirmPassword,
} from "../../utils.js";
import { ENV_CONFIG } from "../../config/environment-unified";

//redux
import { useDispatch } from "react-redux";
import { actionCreators } from "../../state/index";
import { bindActionCreators } from "redux";

// Import Icons
import { ReactComponent as UserIcon } from "../../img/icons/single-user.svg";
import { ReactComponent as GoogleIcon } from "../../img/icons/social-media/google.svg";
// import { ReactComponent as AppleIcon } from "../../img/icons/social-media/apple.svg";
// import { ReactComponent as TwitterIcon } from "../../img/icons/social-media/twitter.svg";

// import { logout } from "../../state/action-creator";
// import FacebookLogin from "react-facebook-login";

import { API_ENDPOINTS, RECAPTCHA_CONFIG } from "../../config/api";

const Auth = ({ tabIndex, hideModal }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(tabIndex);
  const list = [t('auth.login'), t('auth.signup')];
  return (
    <>
      <NavTab full list={list} index={index} setIndex={setIndex} />
      {index === 1 && <Login hideModal={hideModal} />}
      {index === 2 && <SignUp hideModal={hideModal} />}
    </>
  );
};
export default Auth;

// Login Form
const Login = ({ hideModal }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const recaptchaRef = useRef();
  const dispatch = useDispatch();
  const { login, logout } = bindActionCreators(actionCreators, dispatch);

  const [data, setData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({
    username: "",
    password: "",
    captcha: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "username":
        setData({ ...data, username: value });
        break;
      case "password":
        setData({ ...data, password: value });
        break;
      case "rememberMe":
        setData({ ...data, rememberMe: !data.rememberMe });
        break;
      default:
        break;
    }
  };

  const ENABLE_RECAPTCHA = RECAPTCHA_CONFIG.ENABLED;

  const formValid = (captchaToken) => {
    setErrors({
      username: validLoginField(data.username),
      password: validPassword(data.password),
      captcha: ENABLE_RECAPTCHA && captchaToken?.length === 0 ? "Captcha is required" : "",
    });

    if (
      validLoginField(data.username).length === 0 &&
      validPassword(data.password).length === 0 &&
      (!ENABLE_RECAPTCHA || captchaToken?.length !== 0)
    )
      return true;
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // I retrienve captcha token value here it return empty string when the captcha is not complete or timed out
    // you can also handle the token expiry on the ReCAPTCHA component by using onExpired prop

    // Utiliser le vrai token reCAPTCHA
    const captchaToken = recaptchaRef.current?.getValue();

    console.log("CAPTCHA TOKEN:", captchaToken); // <- ici
    console.log("ENABLE_RECAPTCHA:", ENABLE_RECAPTCHA); // <- debug
    console.log("HOSTNAME:", window.location.hostname); // <- debug

    if (!formValid(captchaToken)) return;
    const user = {
      login: data.username,
      password: data.password,
      rememberMe: data.rememberMe,
      captchaToken: captchaToken,
    };
    console.log("USER DATA SENT:", user); // <- ici

    axios
      .post(API_ENDPOINTS.LOGIN, user, {
        withCredentials: true,
      })
      .then((res) => {
        if (res.data.success === true) {
          if (res.data.firstLogin) {
            toast.success(t('auth.welcomeMessage'));
            axios
              .post(
                API_ENDPOINTS.INIT_CHANNEL,
                {},
                { withCredentials: true }
              )
              .then((res) => {
                toast.info(res.data.message);
              });
          }
          axios(API_ENDPOINTS.AUTH_ME, {
            method: "get",
            withCredentials: true,
          })
            .then((res) => {
              login(res.data);
              window.location.reload();
            })
            .catch((err) => {
              if (err.response?.status === 401) logout();
              else toast.error(err?.response.data.message);
            });
        } else {
          toast.error(t('auth.loginFailed'));
        }
      })
      .catch((err) => {
        console.log("Login error:", err);

        if (!err.response) {
          toast.error(t('auth.networkError'));
          return;
        }

        const { field, msg } = err.response.data || {};

        if (field && msg) {
          setErrors((prev) => ({ ...prev, [field]: msg }));
        } else if (msg) {
          toast.error(msg);
        } else {
          toast.error(t('auth.invalidCredentials'));
        }
      });
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <FieldGroup label={t('auth.emailOrUsername')}>
        <InputGroup
          name="username"
          placeholder={t('auth.enterUsername')}
          type="text"
          iconLeft={UserIcon}
          alert={errors.username}
          onChange={handleChange}
          autoFocus
        />
      </FieldGroup>
      <FieldGroup label={t('auth.password')} link hideModal={hideModal}>
        <InputPassword
          name="password"
          placeholder={t('auth.enterPassword')}
          alert={errors.password}
          onChange={handleChange}
        />
      </FieldGroup>
      <Checkbox
        name="rememberMe"
        checked={data.rememberMe}
        onChange={handleChange}
      >
        {t('auth.rememberMe')}
      </Checkbox>
      <CaptchaStyle
        sitekey={RECAPTCHA_CONFIG.SITE_KEY}
        ref={recaptchaRef}
        theme={theme.mode}
        alert={errors.captcha}
      />
      <Button type="submit" variant="primary">
        {t('auth.login')}
      </Button>
      <Separator>{t('auth.or')}</Separator>
      <SocialLogin title={t('auth.loginWith')} />
    </Form>
  );
};

// Sign Up Form
const SignUp = ({ hideModal }) => {
  const [data, setData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    agree: false,
  });
  const [errors, setErrors] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    agree: false,
  });

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleAgree = (e) => {
    setData({ ...data, agree: !data.agree });
  };

  const formValid = () => {
    setErrors({
      ...errors,
      username: validUsername(data.username),
      password: validPassword(data.password),
      confirmPassword: checkComfirmPassword(
        data.password,
        data.confirmPassword
      ),
      email: validateEmail(data.email),
      agree: data.agree ? false : true,
    });

    if (
      validUsername(data.username).length === 0 &&
      validPassword(data.password).length === 0 &&
      checkComfirmPassword(data.password, data.confirmPassword).length === 0 &&
      validateEmail(data.email).length === 0 &&
      data.agree
    )
      return true;
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log("🔄 [SIGNUP] Form submission started");
    console.log("🔄 [SIGNUP] Form data:", data);
    
    const isValid = formValid();
    console.log("🔄 [SIGNUP] Form validation result:", isValid);
    console.log("🔄 [SIGNUP] Current errors:", errors);
    
    if (!isValid) {
      console.log("❌ [SIGNUP] Form validation failed, stopping submission");
      return;
    }
    console.log("🔄 [SIGNUP] Attempting signup with endpoint:", ENV_CONFIG.SIGNUP_ENDPOINT);
    console.log("🔄 [SIGNUP] Form data:", { username: data.username, email: data.email });
    
    axios
      .post(
        ENV_CONFIG.SIGNUP_ENDPOINT,
        {
          password: data.password,
          username: data.username,
          email: data.email,
        },
        { withCredentials: true }
      )
      .then((res) => {
        console.log("✅ [SIGNUP] Success response:", res.data);
        if (res.data.success) {
          toast.success("Account created! Please check your email to verify your account.");
          hideModal(); // Close the modal on successful signup
        }
      })
      // .catch((err) => {
      //   setErrors({
      //     password: errors.password,
      //     [err.response.data?.errors[0]?.field]:
      //       err.response.data?.errors[0]?.msg,
      //     [err.response.data?.errors[1]?.field]:
      //       err.response.data?.errors[1]?.msg,
      //   });
      // });
      .catch((err) => {
        console.error("❌ [SIGNUP] Error occurred:", err);
        console.error("❌ [SIGNUP] Response:", err.response);
        console.error("❌ [SIGNUP] Response data:", err.response?.data);
        console.error("❌ [SIGNUP] Response errors:", err.response?.data?.errors);
        
        if (!err.response) {
          toast.error("Network error - please check your connection.");
          return;
        }

        const apiErrors = err.response.data?.errors;

        if (Array.isArray(apiErrors)) {
          const newErrors = {};
          apiErrors.forEach(({ field, msg }) => {
            if (field && msg) {
              newErrors[field] = msg;
              console.log(`❌ [SIGNUP] Field error - ${field}: ${msg}`);
            }
          });
          setErrors((prev) => ({ ...prev, ...newErrors }));
          toast.error("Please fix the form errors and try again.");
        } else if (err.response.data?.msg) {
          toast.error(err.response.data.msg);
        } else {
          toast.error(`Signup failed: ${err.response.status} ${err.response.statusText}`);
        }
      });



  };

  return (
    <>
      <Form onSubmit={handleSubmit} noValidate autocomplete="off">
        <FieldGroup label="username">
          <InputGroup
            name="username"
            onChange={handleChange}
            alert={errors.username}
            placeholder="Enter your username"
            type="text"
            iconLeft={UserIcon}
          />
        </FieldGroup>
        <FieldGroup label="password">
          <InputPassword
            name="password"
            placeholder="Enter password"
            alert={errors.password}
            onChange={handleChange}
          />
        </FieldGroup>
        <FieldGroup label="Confirm Password">
          <InputPassword
            name="confirmPassword"
            placeholder="Confirm password"
            alert={errors.confirmPassword}
            onChange={handleChange}
          />
        </FieldGroup>
        <FieldGroup label="email">
          <InputGroup
            name="email"
            placeholder="example@gmail.com"
            type="email"
            iconLeft={UserIcon}
            alert={errors.email}
            onChange={handleChange}
          />
        </FieldGroup>
        <Checkbox
          name="agree"
          checked={data.agree}
          alert={errors.agree}
          onChange={handleAgree}
        >
          I agree to the{" "}
          <Link to="/legal/cgu" onClick={hideModal}>
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/legal/rgpd" onClick={hideModal}>
            Privacy Policy
          </Link>
        </Checkbox>
        <Button type="submit" variant="primary">
          Sign Up
        </Button>
        <Separator>OR</Separator>
        <SocialLogin title="Sign Up with" />
      </Form>
    </>
  );
};
const SocialLogin = ({ title }) => {
  const dispatch = useDispatch();
  const { login, logout } = bindActionCreators(actionCreators, dispatch);

  // Popup-based OAuth flow
  const openOAuthPopup = (url) => {
    const width = 520; const height = 640;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
    const popup = window.open(
      url,
      'oauth_popup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=no`
    );

    if (!popup) {
      toast.info('Popup blocked. Redirecting...');
      window.location.href = url;
      return;
    }

    const timer = setInterval(async () => {
      try {
        if (popup.closed) {
          clearInterval(timer);
          return;
        }
        // When the popup returns to our origin, we can read its URL
        const sameOrigin = popup.location.origin === window.location.origin;
        if (!sameOrigin) return; // still at provider domain

        const params = new URLSearchParams(popup.location.search);
        const oauthSuccess = params.get('oauthsuccess');
        const firstLogin = params.get('firstlogin') === 'true';

        // Only proceed when backend callback redirected with flags
        if (oauthSuccess) {
          clearInterval(timer);
          try {
            const me = await axios.get(API_ENDPOINTS.AUTH_ME, { withCredentials: true });
            login(me.data.user || me.data);
            toast.success('Login Success');
            if (firstLogin) {
              try {
                const init = await axios.post(API_ENDPOINTS.INIT_CHANNEL, {}, { withCredentials: true });
                if (init?.data?.message) toast.info(init.data.message);
              } catch (_) {}
            }
          } catch (err) {
            if (err?.response?.status === 401) logout();
          } finally {
            popup.close();
          }
        }
      } catch (_) {
        // Cross-origin while on provider domain; ignore until it comes back
      }
    }, 300);
  };

  const handleGoolgleLogin = (e) => {
    e.preventDefault();
    openOAuthPopup(API_ENDPOINTS.GOOGLE_OAUTH);
  };
  return (
    <SocialLoginStyle>
      <span>{title}</span>
      {/* <button style={{ backgroundColor: "#383838" }} onClick={handleFacebookLogin}>
        <AppleIcon />
      </button> */}
      <button
        style={{ backgroundColor: "#C75231" }}
        onClick={handleGoolgleLogin}
      >
        <GoogleIcon />
      </button>
      {/* <button style={{ backgroundColor: "#55B3EF" }} onClick={handleFacebookLogin}>
        <TwitterIcon />
      </button> */}
      {/*
        Facebook connection disabled:
        <button
          style={{ backgroundColor: "#445DA0" }}
          onClick={handleFacebookLogin}
        >
          <FacebookIcon />
        </button>
      */}
      {/* <FacebookLogin
      appId="921201001964201"
      autoLoad={true}
      fields="name,email,picture"
      scope="public_profile,user_friends"
      callback={responseFacebook}
      icon="fa-facebook" /> */}
    </SocialLoginStyle>
  );
};

const CaptchaStyle = styled(ReCAPTCHA)`
  margin-top: 15px;
  > div > div {
    margin: 0 auto;
    ${({ alert }) =>
    alert?.length !== 0 &&
    `
      border: 1px solid #EE4C50;
      border-radius: 3px;
    `};
  }
`;

const SocialLoginStyle = styled.div`
  text-align: center;
  & > span {
    font-size: 10px;
    color: ${({ theme }) => theme.color.lighterText};
    display: block;
    margin-bottom: 15px;
  }
  button {
    width: 58px;
    border-radius: 8px;
    padding: 7px;
    :not(:last-child) {
      margin-right: 15px;
    }
    :hover {
      transform: scale(0.98);
      transition: transform 200ms ease;
    }
  }
`;
const Form = styled.form.attrs({ autocomplete: "off" })`
  & > div {
    width: 100%;
  }
  & > button {
    margin-top: 15px;
    width: 100%;
  }
`;
export const Separator = styled.span`
  text-align: center;
  display: block;
  margin: 15px auto;
  color: ${({ theme }) => theme.color.lighterText};
  font-size: 10px;
  position: relative;
  ::before,
  ::after {
    content: "";
    position: absolute;
    height: 1px;
    background-color: ${({ theme }) => theme.color.border};

    top: 7px;
    width: 45%;
  }
  ::after {
    left: 0;
  }
  ::before {
    right: 0;
  }
`;
